import BlockTitle from "@src/components/_shared/UI/BlockTitle";
import useNavigationStore from "@src/stores/navigationStore";
import useRegistrationStore from "@src/stores/registrationStore";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import Input from "@src/components/_shared/UI/Input";
import ValidateButton from "@src/components/_shared/UI/ValidateButton";
import DropdownList from "@src/components/_shared/UI/DropdownList";
import { useMutation, useQuery } from "@tanstack/react-query";
import { getReferencialData } from "@src/api/Referencial";
import { useEffect, useState } from "react";
import { authUserRoles, userSubscription } from "@src/api/Auth";
import { RegistrationNameSchema } from "@src/utilities/schemas";
import Container from "@src/components/_shared/Container";
import PhoneInput from "@src/components/_shared/UI/PhoneInput";
import { extractPhoneNumber, formattedPhoneNumber, handleApiError } from '@src/utilities/functions';
import { CountryType, UserRoleType } from "@src/utilities/types";
import { Toaster } from 'react-hot-toast';
import { loginCheck } from "@src/api/Auth";
import Loader from "@src/components/_shared/UI/Loader";
import styles from './register.module.scss';
import { personUpdate } from "@src/api/Registration";

export default function Register() {
    const { setSection, setBlock } = useNavigationStore();
    const { setField, registration } = useRegistrationStore();
    const [countryCodeArray, setCountryCodeArray] = useState<string[]>([]);

    const { civility, phone, firstname, lastname, birthname, email, password } = registration.person.basic;
    const registerValidationSchema = registration.partner_informations.is_partner
        ? RegistrationNameSchema.shape({ knownBy: Yup.string().notRequired() })
        : RegistrationNameSchema;
    const { mutate: MutateLogin, isPending: isMutateLoginPending } = useMutation({
        mutationFn: () => loginCheck({login: email, password}),
        onSuccess: async () => {
            setSection(2);
            setBlock('USPerson');
            const userRole = await authUserRoles();
            if (userRole) {
                setField('user_role', userRole.name);
            }
            setField('person.basic.password', '')
        },
        onError: (error: any) => handleApiError(error)
    });

    const { mutate: updatePerson, isPending: updatePersonPending } = useMutation({
        mutationFn: () => personUpdate({key: 'basic', value: registration.person.basic, is_psi: registration.partner_informations.is_partner, person_id: registration.person_id}),
        onSuccess: () => {
            setSection(2);
            setBlock('USPerson');
        },
        onError: (error: any) => handleApiError(error)
    });

    const { mutate, isPending } = useMutation({
        mutationKey: ['userSubscription'],
        mutationFn: () => userSubscription(civility, phone, firstname, lastname, birthname, email, password), 
        onSuccess: (response: any) => {
            MutateLogin();
            setField('figure_id', response.data.id);
        },
        onError: (error: any) => handleApiError(error)
    });

    const { data, isLoading } = useQuery({
        queryKey: ['combinedReferencialData'],
        queryFn: async () => {
            const [civilitiesData, countriesData, knownByData] = await Promise.all([
                getReferencialData({ type: 'civilities' }),
                getReferencialData({ type: 'countries' }),
                getReferencialData({ type: 'origin/known-by' }),
            ]);
            return { civilitiesData, countriesData, knownByData };
        }
    });

    useEffect(() => {
        if (data?.countriesData) {
            setCountryCodeArray(data.countriesData.map((obj: { phone_extension: string; }) => {return obj.phone_extension}))
        }
    }, [data?.countriesData]);

    if (!data || !data.countriesData || !data.civilitiesData || countryCodeArray.length === 0) return null
    if (isLoading) {
        <Container>
            <Loader type="SPINNER" />
        </Container>
    }

    return (
        <Container>
            <BlockTitle title="Veuillez renseigner l’identité du représentant légal" />
            <div style={{ height: '10px' }}/>
            {data?.countriesData && data?.civilitiesData && countryCodeArray.length > 0 ?
                <div className={styles.registerContainer}>
                    <Formik
                        initialValues={{
                            civility: registration.person.basic.civility,
                            firstname: registration.person.basic.firstname,
                            lastname: registration.person.basic.lastname,
                            birthname: registration.person.basic.birthname,
                            phone: extractPhoneNumber(countryCodeArray, registration.person.basic.phone).extractPhone,
                            country: data?.countriesData?.find((obj: any) => obj.phone_extension === extractPhoneNumber(countryCodeArray, registration.person.basic.phone).countryCode) || { name: 'France', alpha2: 'FR', alpha3: 'FRA', numeric: 250, is_ue: true, phone_extension: '+33', rank: 0 },
                            knownBy: registration.person.channel.known_by
                        }}
                        onSubmit={(values) => {
                            const lastname = values.lastname || values.birthname;
                            const formattedPhone = (values.phone).startsWith('+') ? values.phone : `${values.country.phone_extension}${formattedPhoneNumber(values.country, values.phone)}`;
                            const { phone, country, knownBy, ...otherValues } = values;
                            const finalValues = {
                                ...otherValues,
                                lastname,
                                phone: formattedPhone,
                            };
                            Object.entries(finalValues).map(([key, value]) => setField(`person.basic.${key}`, value));
                            setField('person.channel.known_by', values.knownBy);

                            const userRole = registration.user_role as UserRoleType;
                            if (registration.person_id || [UserRoleType.Premium, UserRoleType.Member].includes(userRole)) {
                                return updatePerson();
                            }
                            if (registration.partner_informations.is_partner && !registration.person_id) {
                                setSection(2);
                                setBlock('USPerson');
                                return;
                            }
                            mutate();
                        }}
                        validationSchema={registerValidationSchema}
                    >
                        {({ handleChange, handleSubmit, values, errors, setFieldValue, isValid, touched }) => {
                            return(
                                <Form id="registerForm" className="registerForm" autoComplete="off">
                                    <div style={{ alignSelf: 'flex-start'}}>
                                        <DropdownList
                                            title="Civilité"
                                            width="100%"
                                            options={data?.civilitiesData}
                                            selected={values.civility}
                                            onSelect={handleChange('civility')}
                                            hasError={(!!errors.civility) as boolean} />
                                    </div>
                                    <Input
                                        title="Prénom"
                                        width="100%"
                                        value={values.firstname}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            const capitalizedValue = value ? value.charAt(0).toUpperCase() + value.slice(1) : '';
                                            handleChange('firstname')(capitalizedValue);
                                        }}
                                        placeholder="ex : Jean"
                                        id="firstname"
                                        name="firstname"
                                        type="text"
                                        clearInput={() => handleChange('firstname')('')}
                                        hasError={(values.firstname && !!errors.firstname) as boolean}
                                        errorMessage={errors.firstname}
                                    />
                                    <Input
                                        title="Nom de naissance"
                                        width="100%"
                                        value={values.birthname}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            const capitalizedValue = value ? value.charAt(0).toUpperCase() + value.slice(1) : '';
                                            handleChange('birthname')(capitalizedValue);
                                        }}
                                        placeholder="ex : Dupond"
                                        id="birthname"
                                        name="birthname"
                                        type="text"
                                        clearInput={() => handleChange('birthname')('')}
                                        hasError={(values.birthname && !!errors.birthname) as boolean}
                                        errorMessage={errors.birthname}
                                    />
                                    <Input
                                        title="Autre Nom (optionnel)"
                                        width="100%"
                                        value={values.lastname}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            const capitalizedValue = value ? value.charAt(0).toUpperCase() + value.slice(1) : '';
                                            handleChange('lastname')(capitalizedValue);
                                        }}
                                        placeholder="ex : Dupont"
                                        id="lastname"
                                        name="lastname"
                                        type="text"
                                        clearInput={() => handleChange('lastname')('')}
                                        hasError={(values.lastname && !!errors.lastname) as boolean}
                                        errorMessage={errors.lastname}
                                    />
                                    <PhoneInput
                                        title="Numéro de téléphone"
                                        width="100%"
                                        countries={data?.countriesData}
                                        value={values.phone}
                                        onChange={handleChange('phone')}
                                        placeholder="Numéro de téléphone"
                                        clearInput={() => handleChange('phone')('')}
                                        hasError={(values.phone && !!errors.phone) as boolean}
                                        onCountryChange={(country: CountryType) => setFieldValue('country', country)}
                                        selectedCountry={values.country}
                                        errorMessage={errors.phone}
                                    />
                                    {!registration.partner_informations.is_partner &&
                                        <div style={{ width: '100%'}}>
                                            <DropdownList
                                                title="Comment nous avez-vous connu ?"
                                                width="100%"
                                                options={data?.knownByData}
                                                selected={values.knownBy}
                                                onSelect={handleChange('knownBy')}
                                                hasError={(!!errors.knownBy && touched.knownBy) as boolean} />
                                        </div>}
                                    <ValidateButton title="Continuer" onClick={handleSubmit} onLoading={isPending || updatePersonPending || isMutateLoginPending} disabled={!isValid || isPending || updatePersonPending || isMutateLoginPending}/>
                                </Form>
                            )
                        }}
                    </Formik>
                </div>
                :
                null
            }
            <Toaster/>
        </Container>
    )
}
